import { ReactElement } from 'react';
import { Stack } from '@mui/material';

import { IHospitalDetails, IHospitalHistoryItem } from '~/services/api/hospital/types';
import Loader from '~/ui/components/common/Loader';
import NoDataFound from '~/ui/components/reusable/NoDataFound';
import DetailsSection from '../../DetailsModal/DetailsSection';
import {
  getEmergencyContactAndInsuranceSection,
  getFacilityAndProviderSection,
  getMedicalInformationSection,
  getPatientInformationSection,
} from '../../DetailsModal/utils';
import HistoryList from '../HistoryList';

import styles from './HistoryContent.module.scss';

interface IProps {
  isLoading: boolean;
  items: IHospitalHistoryItem[];
  expandedItemId: number | null;
  loadingDetailsIds: number[];
  detailsByHistoryId: Record<number, IHospitalDetails>;
  onAccordionChange: (item: IHospitalHistoryItem, isExpanded: boolean) => void;
}

const HistoryContent = ({
  isLoading,
  items,
  expandedItemId,
  loadingDetailsIds,
  detailsByHistoryId,
  onAccordionChange,
}: IProps): ReactElement => {
  const renderHistoryDetails = (details: IHospitalDetails) => (
    <Stack spacing={4} className={styles.detailsContent}>
      <DetailsSection {...getPatientInformationSection(details)} />
      <DetailsSection {...getFacilityAndProviderSection(details)} />
      <DetailsSection {...getMedicalInformationSection(details)} />
      <DetailsSection {...getEmergencyContactAndInsuranceSection(details)} />
    </Stack>
  );

  const renderAccordionDetails = (
    historyDetails: IHospitalDetails | null,
    isDetailsLoading: boolean,
  ) => {
    if (isDetailsLoading) {
      return (
        <div className={styles.loaderWrapper}>
          <Loader />
        </div>
      );
    }

    if (historyDetails) {
      return renderHistoryDetails(historyDetails);
    }

    return <div className={styles.emptyState}>Details are unavailable for this history event.</div>;
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderWrapper}>
          <Loader />
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className={styles.container}>
        <NoDataFound />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <HistoryList
        items={items}
        expandedItemId={expandedItemId}
        loadingDetailsIds={loadingDetailsIds}
        detailsByHistoryId={detailsByHistoryId}
        onAccordionChange={onAccordionChange}
        renderAccordionDetails={renderAccordionDetails}
      />
    </div>
  );
};

export default HistoryContent;
